import React from 'react';
import { Button } from '../components/ui/button';
import { LogOut, ShoppingCart, Printer } from 'lucide-react';
import PremiumHeader from '../components/brand/PremiumHeader';
import BackButton from '../components/BackButton';
import PrintInstitutionalFrame from '../components/PrintInstitutionalFrame';
import '../styles/print-institutional.css';
import { DashboardSidebar, DashboardContent } from '../components/ui/dashboard-sidebar';
import NotificationBell from '../components/NotificationBell';
import LanguageSelector from '../components/LanguageSelector';
import HelpCenterV2 from '../components/HelpCenterV2';
import WelcomeTour from '../components/WelcomeTour';
import AdhesionFTPEBanner from '../components/AdhesionFTPEBanner';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import CreditBanner from '../components/CreditBanner';
import { useCooperDashboard } from './useCooperDashboard';
import CooperDashboardTabs from './CooperDashboardTabs';

export default function CooperDashboard() {
  const {
    user, getAuthHeader, navigate,
    demandes, loading, cooperData, setCooperData,
    activeTab, setActiveTab, fetchData, handleLogout, sidebarTabs,
  } = useCooperDashboard();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coppam-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-print-mode="institutional">
      <PrintInstitutionalFrame
        docTitle="Tableau de Bord COOPER'S"
        caption="Espace Cooper"
        captionSecondary="Délégué Coopérative"
      />
      {/* Bandeau Crédits ESS */}
      <CreditBanner />

      {/* Bandeau Adhésion FTPE */}
      <AdhesionFTPEBanner />

      {/* Annonces & Communications (visibles par les Coopers) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <AnnouncementsBanner space="cooper" />
      </div>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-[52px] z-40 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <BackButton />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-coppam-blue truncate">Espace COOPER'S</h1>
                <p className="text-sm text-slate-600 truncate"><span className="font-semibold text-coppam-gold">{user?.nom} {user?.prenom}</span></p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <LanguageSelector variant="compact" />
              <NotificationBell />
              <Button
                size="sm"
                variant="outline"
                onClick={() => { try { window.print(); } catch {} }}
                className="border-amber-300/60 text-amber-700 hover:bg-amber-50 print-allow"
                title="Imprimer ou exporter en PDF institutionnel (Ctrl+P)"
                data-testid="print-institutional-btn"
              >
                <Printer className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Imprimer PDF officiel</span>
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm" data-testid="logout-btn">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print-area">
        {/* Bandeau Premium O'SCOP — Bienvenue COOPER'S */}
        <PremiumHeader
          eyebrow="Espace COOPER'S — Conseiller ESS"
          title={`Bienvenue ${user?.entreprise || user?.nom || 'COOPER\'S'}`}
          subtitle="Au sein de la communauté des Conseillers ESS engagés."
          testId="cooper-welcome-premium"
          className="mb-6"
        />

        {/* Carte d'information détaillée (mission & valeurs) */}
        <div className="relative bg-white border border-slate-200/80 rounded-2xl p-6 mb-8 ring-1 ring-[#DEA900]/15">
          <span className="absolute left-0 top-6 bottom-6 w-1 rounded-r-full bg-[#DEA900]" />
          <div className="pl-3 text-slate-700 space-y-3 text-sm leading-relaxed">
            <p>
              <strong className="text-[#0a3d62]">Vous êtes au cœur de l'accompagnement ESS et du soutien coopératif.</strong> Vous faites partie du réseau des Conseillers ESS engagés aux côtés d'
              <strong className="text-[#b88600]">OBJECTIF SCOP OUTREMER</strong>,{' '}
              <strong className="text-[#b88600]">COPPAM</strong> et{' '}
              <strong className="text-[#b88600]">FOGEDOM-SCIC</strong>{' '}
              pour accompagner les entreprises ultramarines, soutenir les projets en difficulté, renforcer la prévention et animer la coopération sur nos territoires.
            </p>
            <p>
              Cet espace met à votre disposition tous vos outils, procédures et ressources pour agir efficacement auprès des membres et renforcer l'entraide sur nos territoires.
            </p>
            <p className="text-[#0a3d62] font-semibold italic pt-1 border-t border-slate-100 mt-3">
              Merci de contribuer chaque jour à la solidarité, à la prévention, à la médiation et à la coopération entrepreneuriale dans nos territoires ultramarins.
            </p>
          </div>
        </div>

        {/* Quick Action - Achats via Virement (premium) */}
        <div className="mb-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] shadow-[0_12px_36px_-12px_rgba(10,61,98,0.4)]">
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
            <div className="relative p-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-[#DEA900] flex items-center justify-center shadow-lg shadow-[#DEA900]/30 ring-1 ring-[#f5cc3e]/40 shrink-0">
                  <ShoppingCart className="h-6 w-6 text-[#0a3d62]" strokeWidth={2.4} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Acheter des Demandes Publiques</h3>
                  <p className="text-[#f5cc3e]/90 text-sm">Accédez aux demandes publiques instantanément</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/cooper/achats-demandes')}
                className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-semibold border-0"
                size="lg"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Voir les demandes
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex gap-4 md:gap-6">
          <DashboardSidebar
            tabs={sidebarTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="no-print"
          />

          <DashboardContent className="space-y-4">
            <CooperDashboardTabs
              activeTab={activeTab}
              user={user}
              getAuthHeader={getAuthHeader}
              demandes={demandes}
              navigate={navigate}
              cooperData={cooperData}
              setCooperData={setCooperData}
              fetchData={fetchData}
            />
          </DashboardContent>
        </div>
      </div>

      {/* Tour guidé de bienvenue */}
      <WelcomeTour space="cooper" />

      {/* Centre d'aide amélioré */}
      <HelpCenterV2 space="cooper" />
    </div>
  );
}
